import {
  FEATURE_COMPARISON_CATEGORIES,
  FEATURE_COMPARISON_PLANS,
} from '@/Domain/components/Subscription/FeatureComparison/featureComparison.config';
import { TierEnum } from '@/Domain/enum/Tier.enum';

export interface UpgradeTier {
  id: TierEnum;
  key: string; // `pricing_step.tiers.<key>.name`
  productKey: string; // `SubscriptionPlansDto.products` key
  displayPrice: string; // `price.monthly` is a "$9" string; the API's price_monthly is a Stripe ID
  articles: number;
}

// Per-tier KB article capacity, read from the feature-comparison `articles_limit` row
// so the upsell can't drift from the pricing matrix.
const ARTICLES_LIMIT_VALUES = FEATURE_COMPARISON_CATEGORIES.flatMap((c) => c.features).find(
  (f) => f.key === 'articles_limit',
)?.values;

const planArticleCapacity = (key: string): number | null => {
  const value = ARTICLES_LIMIT_VALUES?.[key as keyof typeof ARTICLES_LIMIT_VALUES];
  if (value?.type !== 'text') return null; // 'translation' = the unlimited (custom) tier
  const articles = Number(value.text.replace(/[^\d]/g, ''));
  return Number.isFinite(articles) && articles > 0 ? articles : null;
};

// Buyable tiers, ascending by capacity.
export const BUYABLE_UPGRADE_TIERS: readonly UpgradeTier[] = FEATURE_COMPARISON_PLANS.flatMap(
  (plan) => {
    if (plan.tier === null || plan.productKey === null || plan.isCustom) return [];
    const articles = planArticleCapacity(plan.key);
    if (articles === null) return [];
    return [
      {
        id: plan.tier,
        key: plan.key,
        productKey: plan.productKey,
        displayPrice: plan.price.monthly,
        articles,
      },
    ];
  },
);

// Cheapest tier that fits existing articles + the selection AND is a strict
// capacity upgrade over the current plan; null when no buyable plan fits.
export const pickUpgradeTier = (
  tiers: readonly UpgradeTier[],
  requiredCapacity: number,
  currentCapacity: number | null,
): UpgradeTier | null =>
  tiers.find(
    (tier) =>
      tier.articles >= requiredCapacity &&
      (currentCapacity === null || tier.articles > currentCapacity),
  ) ?? null;

export function applyDiscount(price: string, percentOff: number): string | null {
  const match = price.match(/^([^\d]*)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const [, prefix, amount, suffix] = match;
  const raw = Number(amount) * (1 - percentOff / 100);
  // 50% off an odd marketing price ($9, $29…) lands on a half-dollar; show cents so it isn't rounded up to understate the discount.
  const next = Number.isInteger(raw) ? String(raw) : raw.toFixed(2);
  return `${prefix}${next}${suffix}`;
}
